import { Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { debounceTime, filter, Observable, of, Subject, takeUntil } from 'rxjs';
import {
  BACK_OFFICE,
  STATE_TRANSACTION,
  TYPE_TRANSACTION,
} from 'src/app/core/helpers/global/global.constant';
import { TRANSFER_RECEIPT_KEY } from 'src/app/core/helpers/global/financial.constant';
import {
  NG_SELECT_QUERIES,
  PLAYER_CODE_LOOKUP_DEBOUNCE_MS,
  TRANSFER_CODE_MAX_LENGTH,
  WITHDRAWAL_CODE_LOOKUP_DEBOUNCE_MS,
  WITHDRAWAL_CODE_MIN_LENGTH_FOR_LOOKUP,
  swalWithBootstrapButtons,
} from 'src/app/core/helpers/ui/ui.constant';
import { User } from 'src/app/core/interfaces/api/user.interface';
import { NgSelect, NgSelectQuery } from 'src/app/core/interfaces/ui/ui.interface';
import { FinancialService } from 'src/app/core/services/api/financial.service';
import { PlayerService } from 'src/app/core/services/api/player.service';
import { TransferService } from 'src/app/core/services/api/transfer-user.service';
import { UserActions } from 'src/app/core/states/auth/auth.actions';
import { BootstrapModalService } from 'src/app/core/services/ui/bootstrap-modal.service';
import { GlobalService } from 'src/app/core/services/ui/global.service';
import { ImageCompressionService } from 'src/app/core/services/ui/image-compression.service';
import { SearchNgSelectService } from 'src/app/core/services/ui/search-ng-select.service';
import { ToastrNotificationService } from 'src/app/core/services/ui/toastr-notification.service';
import { QrScannerModalComponent } from 'src/app/shared/components/modals/qr-scanner-modal/qr-scanner-modal.component';

interface FilePreview {
  file: File;
  isPreviewVisible: boolean;
  urlFile: string;
}

type ScannerTarget = 'playerCode' | 'withdrawalCode';

@Component({
  selector: 'app-player-transfer-by-code-modal',
  templateUrl: './player-transfer-by-code-modal.component.html',
  styleUrl: './player-transfer-by-code-modal.component.scss',
  providers: [{ provide: 'ngBanks', useClass: SearchNgSelectService }],
})
export class PlayerTransferByCodeModalComponent implements OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef;

  public TYPE_TRANSACTIONS = TYPE_TRANSACTION;
  public NG_SELECT_QUERIES = NG_SELECT_QUERIES;
  public transaction_type: string = TYPE_TRANSACTION.DEPOSIT;
  public transferForm: FormGroup;
  public banks$: Observable<NgSelect<string>[]> = of([]);
  public isSubmitting: boolean = false;
  public isBanking: boolean = false;
  public previewImage: string | undefined;
  public selectedFiles: FilePreview[] = [];
  public profile: User = {} as User;
  public resolvedPlayerId: string | null = null;
  public resolvedPlayerUsername: string | null = null;
  public resolvedPlayerFullName: string | null = null;
  public resolvedPlayerDocumentNumber: string | null = null;
  public resolvedWithdrawalAmount: number | null = null;
  public resolvedWithdrawalCodeId: string | null = null;
  public selectedPlayerBalance: number = 0;
  public selectedPlayerCredit: number = 0;
  public selectedCodeCurrencyPlayer: string = '';
  public selectedIdCurrencyPlayer: string = '';
  public previousBalance: number = 0;
  public currentBalance: number = 0;
  public isLookingUpCode: boolean = false;
  public codeLookupFailed: boolean = false;
  private lastResolvedTarget: ScannerTarget | null = null;
  private lastResolvedCode: string | null = null;

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject('ngBanks') private _ngBanks: SearchNgSelectService<string>,
    private _fb: FormBuilder,
    private _sanitizer: DomSanitizer,
    private _financialService: FinancialService,
    private _transferService: TransferService,
    private _playerService: PlayerService,
    private _modalService: BootstrapModalService<string>,
    private _ngbModal: NgbModal,
    private _globalService: GlobalService,
    private _imageCompressionService: ImageCompressionService,
    private _translateService: TranslateService,
    private _notification: ToastrNotificationService,
    private _store: Store
  ) {
    this.transferForm = this.getConfigForm();
    this.setConfigNgSelect();
    this._globalService.profile.pipe(takeUntil(this.unsubscribe$)).subscribe((profile) => {
      this.profile = profile;
    });

    this.transferForm
      .get('playerCode')
      ?.valueChanges.pipe(
        debounceTime(PLAYER_CODE_LOOKUP_DEBOUNCE_MS),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((code) => this.resolveByCode('playerCode', code));
    this.transferForm
      .get('withdrawalCode')
      ?.valueChanges.pipe(
        debounceTime(WITHDRAWAL_CODE_LOOKUP_DEBOUNCE_MS),
        filter((code) => !code || code.length >= WITHDRAWAL_CODE_MIN_LENGTH_FOR_LOOKUP),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((code) => this.resolveByCode('withdrawalCode', code));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private getConfigForm(): FormGroup {
    return this._fb.group({
      playerCode: [null],
      withdrawalCode: [null],
      amount: [null, [Validators.required, Validators.min(0.01)]],
      comment: [''],
      bankId: [null],
      receipt: [null],
    });
  }

  private setConfigNgSelect(): void {
    this._ngBanks.setSearchTermKey('name');
    this._ngBanks.setFetchDataFunction(
      this._financialService.findBanksForSelect.bind(this._financialService)
    );
    this.banks$ = this._ngBanks.getData();
    this._ngBanks.triggerFetchData();
  }

  public onSearchSelect(type: NgSelectQuery, term?: string): void {
    if (type === NG_SELECT_QUERIES.BANKS) this._ngBanks.searchTerm(term || '');
  }

  public onScrollToEndSelect(type: NgSelectQuery): void {
    if (type === NG_SELECT_QUERIES.BANKS) this._ngBanks.scrollToEnd();
  }

  public onOptionChange(option: string): void {
    if (this.isSubmitting) return;
    this.transaction_type = option;
    this.clearImage();
    this.clearResolvedPlayer();

    if (option === TYPE_TRANSACTION.DEPOSIT) {
      this.transferForm.get('withdrawalCode')?.reset();
      this.transferForm.get('amount')?.setValidators([Validators.required, Validators.min(0.01)]);
    } else {
      this.transferForm.get('playerCode')?.reset();
      this.transferForm.get('amount')?.clearValidators();
      this.transferForm.get('amount')?.reset();
      this.isBanking = false;
    }
    this.transferForm.get('amount')?.updateValueAndValidity();
  }

  private resolveByCode(target: ScannerTarget, code: string | null): void {
    if (
      code &&
      target === this.lastResolvedTarget &&
      code === this.lastResolvedCode &&
      this.resolvedPlayerId
    ) {
      return;
    }

    this.clearResolvedPlayer();
    this.lastResolvedTarget = null;
    this.lastResolvedCode = null;
    if (!code) return;

    this.isLookingUpCode = true;

    if (target === 'playerCode') {
      this._playerService
        .findPlayerByCode(code)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (res) => {
            const player = res.data;
            this.resolvedPlayerId = player._id;
            this.resolvedPlayerUsername = player.username;
            this.resolvedPlayerFullName = `${player.firstName} ${player.lastName}`.trim();
            this.resolvedPlayerDocumentNumber = player.documentNumber;
            this.selectedPlayerBalance = player.balance;
            this.selectedCodeCurrencyPlayer = player.currencyId.name;
            this.selectedIdCurrencyPlayer = player.currencyId._id;
            this.previousBalance = player.balance;
            this.isLookingUpCode = false;
            this.lastResolvedTarget = target;
            this.lastResolvedCode = code;
            this._notification.showNotification({
              type: 'success',
              message: 'transfer.playerFound',
            });
          },
          error: () => {
            this.isLookingUpCode = false;
            this.codeLookupFailed = true;
            this._notification.showNotification({
              type: 'error',
              message: 'transfer.playerIdLookupFailed',
            });
          },
        });
      return;
    }

    this._transferService
      .findPhysicalWithdrawByCode(code)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          const withdraw = res.data;
          const player = withdraw.playerId.personalInfo;
          this.resolvedPlayerId = withdraw.playerId._id;
          this.resolvedPlayerUsername = player.username;
          this.resolvedPlayerFullName = `${player.firstName} ${player.lastName}`.trim();
          this.resolvedPlayerDocumentNumber = player.documentNumber;
          this.resolvedWithdrawalAmount = withdraw.amount;
          this.resolvedWithdrawalCodeId = withdraw._id;
          this.selectedIdCurrencyPlayer = withdraw.currencyId._id;
          this.selectedCodeCurrencyPlayer = withdraw.currencyId.name;
          this.isLookingUpCode = false;
          this.lastResolvedTarget = target;
          this.lastResolvedCode = code;
          this._notification.showNotification({
            type: 'success',
            message: 'transfer.withdrawalCodeFound',
          });
        },
        error: () => {
          this.isLookingUpCode = false;
          this.codeLookupFailed = true;
          this._notification.showNotification({
            type: 'error',
            message: 'transfer.codeLookupFailed',
          });
        },
      });
  }

  public onCodePaste(event: ClipboardEvent, target: ScannerTarget): void {
    let pasted = event.clipboardData?.getData('text')?.trim();
    if (!pasted) return;

    if (target === 'playerCode') {
      pasted = pasted.replace(/\D/g, '');
      if (!pasted) return;
    }

    pasted = pasted.slice(0, TRANSFER_CODE_MAX_LENGTH);

    event.preventDefault();
    this.transferForm.get(target)?.setValue(pasted, { emitEvent: false });
    this.resolveByCode(target, pasted);
  }

  private clearResolvedPlayer(): void {
    this.resolvedPlayerId = null;
    this.resolvedPlayerUsername = null;
    this.resolvedPlayerFullName = null;
    this.resolvedPlayerDocumentNumber = null;
    this.resolvedWithdrawalAmount = null;
    this.resolvedWithdrawalCodeId = null;
    this.selectedPlayerBalance = 0;
    this.selectedPlayerCredit = 0;
    this.selectedCodeCurrencyPlayer = '';
    this.selectedIdCurrencyPlayer = '';
    this.previousBalance = 0;
    this.isLookingUpCode = false;
    this.codeLookupFailed = false;
  }

  public rescanPlayer(): void {
    this.clearResolvedPlayer();
    this.transferForm.get('playerCode')?.reset();
  }

  public openQrScanner(target: ScannerTarget): void {
    const modalRef = this._ngbModal.open(QrScannerModalComponent, {
      size: 'sm',
      centered: true,
    });
    modalRef.result.then(
      (code: string) => {
        if (!code) return;
        this.transferForm.get(target)?.setValue(code, { emitEvent: false });
        this.resolveByCode(target, code);
      },
      () => {}
    );
  }

  public toggleBankingMode(): void {
    this.isBanking = !this.isBanking;

    if (!this.isBanking) {
      this.clearImage();
      this.transferForm.get('receipt')?.clearValidators();
      this.transferForm.get('bankId')?.clearValidators();
      this.transferForm.get('receipt')?.reset();
      this.transferForm.get('bankId')?.reset();
    } else {
      this.transferForm.get('bankId')?.setValidators([Validators.required]);
    }

    this.transferForm.get('receipt')?.updateValueAndValidity();
  }

  async onFileSelected(event: any): Promise<void> {
    const file: File = event.target.files[0];
    if (!file) return;

    const compressedFile = await this._imageCompressionService.compressImage(file, 800, 800, 0.7);
    const url = this.fileUrl(file);

    this.selectedFiles = [{ file: compressedFile, isPreviewVisible: true, urlFile: url }];
    this.previewImage = url;
    event.target.value = '';
  }

  public triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  private fileUrl(file: File): any {
    const url = URL.createObjectURL(file);
    return this._sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    const url = this.fileUrl(file);
    this.selectedFiles = [{ file, isPreviewVisible: true, urlFile: url }];
    this.previewImage = url;
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  private clearImage(): void {
    this.previewImage = undefined;
    this.transferForm.get('receipt')?.clearValidators();
    this.transferForm.get('receipt')?.reset();
    this.transferForm.get('bankId')?.clearValidators();
    this.transferForm.get('bankId')?.reset();
    this.selectedFiles = [];
  }

  public submitForm(): void {
    if (this.transferForm.invalid || this.isSubmitting || !this.resolvedPlayerId) return;

    const isWithdrawal = this.transaction_type === TYPE_TRANSACTION.WITHDRAWAL;
    const amountNumber = isWithdrawal
      ? Number(this.resolvedWithdrawalAmount ?? 0)
      : Number(this.transferForm.value.amount);

    swalWithBootstrapButtons
      .fire({
        title: this._translateService.instant('transfer.confirmSubmitTitle'),
        html: this._translateService.instant(
          isWithdrawal ? 'transfer.confirmWithdrawalMessage' : 'transfer.confirmDepositMessage',
          {
            amount: amountNumber.toFixed(2),
            currency: this.selectedCodeCurrencyPlayer,
            player: `<strong>${this.resolvedPlayerFullName || this.resolvedPlayerUsername}</strong>`,
          }
        ),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this._translateService.instant('btn.confirm'),
        cancelButtonText: this._translateService.instant('btn.cancel'),
      })
      .then((result) => {
        if (result.isConfirmed) this.processSubmit(amountNumber, isWithdrawal);
      });
  }

  private processSubmit(amountNumber: number, isWithdrawal: boolean): void {
    this.isSubmitting = true;
    const formValue = this.transferForm.value;
    this.transferForm.disable({ emitEvent: false });
    const amount = isWithdrawal ? -Math.abs(amountNumber) : amountNumber;
    this.currentBalance = this.previousBalance + amount;

    const filteredData: any = {
      amount: amount.toFixed(2),
      comment: formValue.comment,
      operationType: this.transaction_type,
      type: TYPE_TRANSACTION.CASH,
      cashType: this.isBanking ? TYPE_TRANSACTION.BANKING : TYPE_TRANSACTION.DIRECT,
      registeredBy: this.profile._id,
      stateTransaction: STATE_TRANSACTION.PROCESSED,
      origin: BACK_OFFICE,
      previousBalance: this.previousBalance.toFixed(2),
      currentBalance: this.currentBalance.toFixed(2),
      playerId: this.resolvedPlayerId,
      currencyId: this.selectedIdCurrencyPlayer,
      isPhysicalCash: 'true',
    };

    if (isWithdrawal) {
      filteredData.physicalWithdrawCodeId = this.resolvedWithdrawalCodeId ?? '';
    } else if (formValue.bankId) {
      filteredData.bankId = formValue.bankId;
    }

    const formData = new FormData();
    for (const key in filteredData) {
      if (Object.prototype.hasOwnProperty.call(filteredData, key) && key !== TRANSFER_RECEIPT_KEY) {
        formData.append(key, filteredData[key]);
      }
    }

    const receiptFile = this.selectedFiles[0]?.file;
    if (receiptFile) formData.append(TRANSFER_RECEIPT_KEY, receiptFile);

    this._transferService.saveTransfer(formData).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.closeModal();
        this._store.dispatch(UserActions.loadUserSession());
      },
      error: () => {
        this.isSubmitting = false;
        this.transferForm.enable({ emitEvent: false });
        this.clearResolvedPlayer();
        const codeControlName = isWithdrawal ? 'withdrawalCode' : 'playerCode';
        this.transferForm.get(codeControlName)?.reset(null, { emitEvent: false });
        this.transferForm.get('amount')?.reset(null, { emitEvent: false });
      },
    });
  }

  public getButtonLabel(): string {
    return this.transaction_type === TYPE_TRANSACTION.DEPOSIT
      ? 'words.toDeposit'
      : 'words.toWithdrawal';
  }

  public closeModal(): void {
    this._modalService.closeModal();
  }
}
